import { Icon } from '@sunwebgroup/uds-ui/icon'
import { IconCell } from '@sunwebgroup/uds-ui/icon-cell'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Rating } from '@sunwebgroup/uds-ui/rating'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import {
  Button,
  useBreakpoints,
} from '@sunwebgroup/ui-components/uds'
import { addDays } from 'date-fns'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { type MouseEvent, useRef, useState } from 'react'
import { useUser } from '@/domain/user/hooks'
import { useIntl } from '@/intl'
import { Card } from '@/scopes/common/components/Card'
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage'
import { PriceArea } from '@/scopes/common/components/PriceArea'
import { AccoListerAcco } from '@/scopes/common/domain/acco-lister/types'
import { getBrandName } from '@/scopes/common/infrastructure/analytics/brandNameMapper'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import styles from './AccoCard.module.css'
import { AccoPriceDetails } from './PriceDetails'
import { ReviewScore } from './ReviewScore'

export function AccoCard({ acco, onClickCallback }: { acco: AccoListerAcco, onClickCallback?: (acco: AccoListerAcco, isLoggedIn: boolean, accoBrand: string) => void }) {
  const intl = useIntl()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isLoggedIn } = useUser()
  const { sendEvent } = useAnalytics()
  const brandName = useConfiguration('siteName')
  const accoBrand = getBrandName(brandName)
  const cardRef = useRef<HTMLDivElement>(null)
  const cardInView = useOnScreen(cardRef)
  const { isDesktop } = useBreakpoints()

  const handleModalOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    if (isModalOpen) {
      setIsModalOpen(false)
    }
  }

  // Function coming from react-pages/SearchPage/Results/ProductCard/ScoresRatings.tsx
  // TODO refactor and extract this function to a shared location
  const getUserRating = (userScore: number) => {
    if (userScore >= 8) {
      return intl.formatMessage({
        id: 'product_card_user_rating_fantastic',
      })
    }
    else if (userScore >= 6) {
      return intl.formatMessage({
        id: 'product_card_user_rating_good',
      })
    }
    else if (userScore >= 4) {
      return intl.formatMessage({
        id: 'product_card_user_rating_average',
      })
    }
    else if (userScore >= 2) {
      return intl.formatMessage({
        id: 'product_card_user_rating_reasonable',
      })
    }

    return intl.formatMessage({
      id: 'product_card_user_rating_unsatisfied',
    })
  }

  const tripDates = () => {
    if (!acco.price.departureDate || !acco.price.durationInDays) {
      return null
    }

    const departureDate = new Date(acco.price.departureDate)

    const formatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }

    return `${departureDate.toLocaleDateString(intl.locale, formatOptions as Intl.DateTimeFormatOptions)} -
    ${addDays(departureDate, acco.price.durationInDays - 1).toLocaleDateString(
      intl.locale,
      formatOptions as Intl.DateTimeFormatOptions,
    )}`
  }

  const numberOfPeople = () => {
    const participants = acco.price.participants[0]
    return participants.adults + participants.children.length + participants.babies.length
  }

  const peopleCount = numberOfPeople()

  const onClickHandler = () => {
    if (!onClickCallback) {
      return
    }
    sendEvent(onClickCallback(acco, isLoggedIn, accoBrand))
  }
  return (
    <Card className={styles.accoCard}>
      <a className={styles.overlayLink} href={acco.url} aria-label={acco.name} target={isDesktop ? '_blank' : '_self'} onClick={onClickHandler} />
      <div className={styles.imageContainer} ref={cardRef}>
        <MediaServerLazyImage
          src={acco.imageUrl}
          alt={acco.name}
          widths={[480, 640]}
          loading={!cardInView ? 'lazy' : 'eager'}
          aspectRatio="3:2"
        />
      </div>
      <div className={styles.cardMain}>
        <div className={styles.cardHeader}>
          <div className={styles.ratingContainer}>
            <Rating className={styles.rating} rating={acco.rating} />
            {acco.userRating > 0 && (
              <ReviewScore rating={acco.userRating} scoreLabel={getUserRating(acco.userRating)} />
            )}
          </div>
          <div className={styles.titleContainer}>
            <Typography variant="display-g-regular">{acco.name}</Typography>
            <div className={styles.breadcrumbs}>
              {acco.city && <Typography variant="body-s-regular-tight" className={styles.secondaryText}>{acco.city}</Typography>}
              {acco.region && <Typography variant="body-s-regular-tight" className={styles.secondaryText}>{acco.region}</Typography>}
              {acco.country && <Typography variant="body-s-regular-tight" className={styles.secondaryText}>{acco.country}</Typography>}
            </div>
          </div>

          {acco.usps && !isEmpty(acco.usps) && (
            <ListItem className={styles.uspsWrapper} variant="transparent" as="ul">
              {acco.usps.map(item => (
                <li key={item} className={styles.usp}>
                  <span className={styles.uspIcon}>
                    <IconCell glyph="CheckMarkExtraSmall" verticalAlignment="top" size="sm" />
                  </span>
                  <Typography variant="body-s-regular-tight">{item}</Typography>
                </li>
              ))}
            </ListItem>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.footerDetails}>
            <div className={styles.overview}>
              <div>
                <Typography variant="body-s-bold-snug" className={styles.tripDates}>{tripDates()}</Typography>
              </div>
              <div className={styles.mealplans}>
                {acco.price.mealplan?.name && <Typography variant="body-s-regular-snug">{acco.price.mealplan?.name}</Typography>}
                {peopleCount > 0 && (
                  <div className={styles.amountOfPeople}>
                    <Typography variant="body-s-regular-snug">{peopleCount}</Typography>
                    <Typography variant="body-s-regular-snug">{intl.formatMessage({ id: 'person_abbreviation' })}</Typography>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.priceBoxWrapper}>
              <PriceArea
                price={intl.formatTruncatedCurrency(acco.price.averagePrice)}
                areaText={intl.formatMessage({ id: 'price_box_price_text' }, { isPerPerson: true })}
                size="S"
                discounts={
                  acco.price.discount && acco.price.discount.percentage > 0
                    ? {
                        discountLabel: `-${acco.price.discount.percentage}%`,
                        originalPrice: intl.formatTruncatedCurrency(acco.price.discount.originalPrice),
                      }
                    : undefined
                }

              >
                {acco.price.showAcmInformation && (
                  <>
                    <button type="button" className={styles.iconButton} onClick={handleModalOpen} aria-label={intl.formatMessage({ id: 'price_box_price_info' })}>
                      <Icon glyph="InformationInCircle" size={16} />
                    </button>
                    {isModalOpen
                      ? (
                          <AccoPriceDetails onClose={handleModalClose} price={acco.price} accommodationId={acco.id} />
                        )
                      : null}
                  </>
                )}
              </PriceArea>
            </div>
          </div>
          <Button size="M" variant="primary" fullWidth>{intl.formatMessage({ id: 'product_card_view_button_text' })}</Button>
        </div>
      </div>
    </Card>
  )
}

AccoCard.displayName = 'AccoCard'
